import { useState, useEffect } from 'react'
import { Card, Table, Button, Badge, Row, Col, Alert, Spinner, Modal, ListGroup, Form } from 'react-bootstrap'
import { toast } from 'react-toastify'
import api from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface BackupInfo {
  filename: string
  type: string
  size: number
  size_human: string
  created: string
  path: string
}

interface BackupListResponse {
  daily: BackupInfo[]
  weekly: BackupInfo[]
  monthly: BackupInfo[]
  total_size: string
}

interface BackupStatus {
  running: boolean
  last_backup: string | null
  next_daily: string | null
  next_weekly: string | null
  next_monthly: string | null
}

interface BackupDetails {
  filename: string
  type: string
  size: number
  size_human: string
  created: string
  file_count?: number
  meta?: {
    backup_type: string
    timestamp: string
    date: string
    hostname: string
    astgui_version: string
    asterisk_version: string
    components: Record<string, boolean>
  }
}

interface RestoreComponent {
  id: string
  name: string
  description: string
  available: boolean
  category: string
}

interface RestoreResult {
  component: string
  success: boolean
  output?: string
  error?: string
}

export default function SystemBackups() {
  const [backups, setBackups] = useState<BackupListResponse | null>(null)
  const [status, setStatus] = useState<BackupStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [selectedBackup, setSelectedBackup] = useState<BackupDetails | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const confirm = useConfirm()

  // Restore state
  const [showRestore, setShowRestore] = useState(false)
  const [restoreBackup, setRestoreBackup] = useState<{ type: string; filename: string } | null>(null)
  const [restoreComponents, setRestoreComponents] = useState<RestoreComponent[]>([])
  const [selectedComponents, setSelectedComponents] = useState<string[]>([])
  const [restoring, setRestoring] = useState(false)
  const [restoreResults, setRestoreResults] = useState<RestoreResult[] | null>(null)
  const [previewMode, setPreviewMode] = useState(true)

  // Upload state
  const [showUpload, setShowUpload] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadType, setUploadType] = useState<string>('daily')
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    loadData()
    loadRestoreComponents()
    // Refresh status every 10 seconds while backup might be running
    const interval = setInterval(() => {
      loadStatus()
    }, 10000)
    return () => clearInterval(interval)
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [backupsRes, statusRes] = await Promise.all([
        api.get('/system-backups/'),
        api.get('/system-backups/status')
      ])
      setBackups(backupsRes.data)
      setStatus(statusRes.data)
    } catch {
      toast.error('Fehler beim Laden der Backups')
    } finally {
      setLoading(false)
    }
  }

  const loadStatus = async () => {
    try {
      const res = await api.get('/system-backups/status')
      setStatus(res.data)
    } catch {
      // Ignore errors in status polling
    }
  }

  const loadRestoreComponents = async () => {
    try {
      const res = await api.get('/system-backups/restore/components')
      setRestoreComponents(res.data.components)
    } catch {
      console.error('Failed to load restore components')
    }
  }

  const handleCreateBackup = async (type: string) => {
    const confirmed = await confirm({
      title: `${type.charAt(0).toUpperCase() + type.slice(1)}-Backup erstellen`,
      message: `Möchten Sie ein neues ${type}-Backup erstellen? Dies kann einige Minuten dauern.`,
      confirmText: 'Backup starten',
      variant: 'primary'
    })
    if (!confirmed) return

    setCreating(true)
    try {
      await api.post('/system-backups/create', { type })
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)}-Backup wird erstellt...`)
      // Poll for completion
      setTimeout(loadData, 5000)
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail || 'Fehler beim Starten des Backups')
    } finally {
      setCreating(false)
    }
  }

  const handleDownload = (type: string, filename: string) => {
    window.open(`/api/system-backups/download/${type}/${filename}`, '_blank')
  }

  const handleDelete = async (type: string, filename: string) => {
    const confirmed = await confirm({
      title: 'Backup löschen',
      message: `Möchten Sie das Backup "${filename}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return

    try {
      await api.delete(`/system-backups/${type}/${filename}`)
      toast.success('Backup gelöscht')
      loadData()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const handleShowDetails = async (type: string, filename: string) => {
    try {
      const res = await api.get(`/system-backups/info/${type}/${filename}`)
      setSelectedBackup(res.data)
      setShowDetails(true)
    } catch {
      toast.error('Fehler beim Laden der Details')
    }
  }

  const handleOpenRestore = (type: string, filename: string) => {
    setRestoreBackup({ type, filename })
    setSelectedComponents([])
    setRestoreResults(null)
    setPreviewMode(true)
    setShowRestore(true)
  }

  const handleUpload = async () => {
    if (!uploadFile) return

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', uploadFile)
      formData.append('backup_type', uploadType)

      await api.post('/system-backups/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      toast.success('Backup erfolgreich hochgeladen')
      setShowUpload(false)
      setUploadFile(null)
      loadData()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail || 'Fehler beim Hochladen')
    } finally {
      setUploading(false)
    }
  }

  const toggleComponent = (componentId: string) => {
    setSelectedComponents(prev => {
      if (prev.includes(componentId)) {
        return prev.filter(c => c !== componentId)
      } else {
        return [...prev, componentId]
      }
    })
  }

  const selectAllComponents = () => {
    setSelectedComponents(restoreComponents.map(c => c.id))
  }

  const selectCategoryComponents = (category: string) => {
    const categoryComponents = restoreComponents.filter(c => c.category === category).map(c => c.id)
    const allSelected = categoryComponents.every(id => selectedComponents.includes(id))

    if (allSelected) {
      setSelectedComponents(prev => prev.filter(id => !categoryComponents.includes(id)))
    } else {
      setSelectedComponents(prev => [...new Set([...prev, ...categoryComponents])])
    }
  }

  const handleRestore = async () => {
    if (!restoreBackup || selectedComponents.length === 0) return

    setRestoring(true)
    setRestoreResults(null)

    try {
      const res = await api.post(
        `/system-backups/restore/${restoreBackup.type}/${restoreBackup.filename}`,
        {
          components: selectedComponents,
          dry_run: previewMode
        }
      )

      setRestoreResults(res.data.results)

      if (!previewMode) {
        if (res.data.success) {
          toast.success('Wiederherstellung erfolgreich abgeschlossen')
        } else {
          toast.warning('Einige Komponenten konnten nicht wiederhergestellt werden')
        }
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail || 'Fehler bei der Wiederherstellung')
    } finally {
      setRestoring(false)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    try {
      return new Date(dateStr).toLocaleString('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateStr
    }
  }

  const formatNextRun = (timestamp: string | null) => {
    if (!timestamp) return '-'
    try {
      const date = new Date(timestamp)
      if (isNaN(date.getTime())) return timestamp
      return date.toLocaleString('de-CH', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return timestamp
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'system': return 'bi-hdd-fill text-danger'
      case 'config': return 'bi-gear-fill text-primary'
      case 'data': return 'bi-folder-fill text-success'
      default: return 'bi-circle'
    }
  }

  const getCategoryName = (category: string) => {
    switch (category) {
      case 'system': return 'System'
      case 'config': return 'Konfiguration'
      case 'data': return 'Daten'
      default: return category
    }
  }

  const renderBackupTable = (backupList: BackupInfo[], type: string) => (
    <Table hover size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Dateiname</th>
          <th>Erstellt</th>
          <th>Größe</th>
          <th className="text-end">Aktionen</th>
        </tr>
      </thead>
      <tbody>
        {backupList.length === 0 ? (
          <tr>
            <td colSpan={4} className="text-center text-muted py-3">
              Keine {type}-Backups vorhanden
            </td>
          </tr>
        ) : (
          backupList.map((b) => (
            <tr key={b.filename}>
              <td className="small">
                <i className="bi bi-archive me-2 text-primary"></i>
                {b.filename.substring(0, 30)}...
              </td>
              <td className="small">{formatDate(b.created)}</td>
              <td className="small">{b.size_human}</td>
              <td className="text-end">
                <Button
                  variant="outline-warning"
                  size="sm"
                  className="me-1"
                  onClick={() => handleOpenRestore(type, b.filename)}
                  title="Wiederherstellen"
                >
                  <i className="bi bi-arrow-counterclockwise"></i>
                </Button>
                <Button
                  variant="outline-info"
                  size="sm"
                  className="me-1"
                  onClick={() => handleShowDetails(type, b.filename)}
                  title="Details"
                >
                  <i className="bi bi-info-circle"></i>
                </Button>
                <Button
                  variant="outline-success"
                  size="sm"
                  className="me-1"
                  onClick={() => handleDownload(type, b.filename)}
                  title="Download"
                >
                  <i className="bi bi-download"></i>
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(type, b.filename)}
                  title="Löschen"
                >
                  <i className="bi bi-trash"></i>
                </Button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </Table>
  )

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Lade Backups...</p>
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="bi bi-hdd me-2"></i>
          System-Backups
        </h4>
        <div>
          <Button
            variant="outline-success"
            className="me-2"
            onClick={() => setShowUpload(true)}
          >
            <i className="bi bi-upload me-1"></i>
            Backup hochladen
          </Button>
          <Button
            variant="outline-primary"
            className="me-2"
            onClick={() => handleCreateBackup('daily')}
            disabled={creating || status?.running}
          >
            {creating ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-plus-lg me-1"></i>}
            Manuelles Backup
          </Button>
          <Button variant="outline-secondary" onClick={loadData}>
            <i className="bi bi-arrow-clockwise"></i>
          </Button>
        </div>
      </div>

      {status?.running && (
        <Alert variant="info" className="d-flex align-items-center">
          <Spinner size="sm" className="me-2" />
          <span>Ein Backup wird gerade erstellt...</span>
        </Alert>
      )}

      {/* Status Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <div className="h4 mb-0 text-primary">{backups?.total_size || '0 B'}</div>
              <small className="text-muted">Gesamtgröße</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <div className="h4 mb-0">
                {(backups?.daily.length || 0) + (backups?.weekly.length || 0) + (backups?.monthly.length || 0)}
              </div>
              <small className="text-muted">Backups gesamt</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <div className="h6 mb-0">{formatDate(status?.last_backup || null)}</div>
              <small className="text-muted">Letztes Backup</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <Badge bg={status?.running ? 'warning' : 'success'} className="px-3 py-2">
                {status?.running ? 'Läuft...' : 'Bereit'}
              </Badge>
              <div><small className="text-muted">Status</small></div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Schedule Info */}
      <Card className="mb-4">
        <Card.Header>
          <i className="bi bi-calendar-check me-2"></i>
          Automatische Backup-Zeitpläne
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <div className="d-flex align-items-center">
                <Badge bg="primary" className="me-2">Täglich</Badge>
                <span className="small">02:00 Uhr</span>
              </div>
              <small className="text-muted">Nächstes: {formatNextRun(status?.next_daily || null)}</small>
              <div className="small text-muted mt-1">Aufbewahrung: 7 Backups</div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-center">
                <Badge bg="info" className="me-2">Wöchentlich</Badge>
                <span className="small">Sonntag 03:00 Uhr</span>
              </div>
              <small className="text-muted">Nächstes: {formatNextRun(status?.next_weekly || null)}</small>
              <div className="small text-muted mt-1">Aufbewahrung: 4 Backups</div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-center">
                <Badge bg="success" className="me-2">Monatlich</Badge>
                <span className="small">1. des Monats 04:00 Uhr</span>
              </div>
              <small className="text-muted">Nächstes: {formatNextRun(status?.next_monthly || null)}</small>
              <div className="small text-muted mt-1">Aufbewahrung: 12 Backups</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Backup Lists */}
      <Row>
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-calendar-day me-2"></i>
                Tägliche Backups
                <Badge bg="secondary" className="ms-2">{backups?.daily.length || 0}</Badge>
              </span>
            </Card.Header>
            <Card.Body className="p-0">
              {renderBackupTable(backups?.daily || [], 'daily')}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-calendar-week me-2"></i>
                Wöchentliche Backups
                <Badge bg="secondary" className="ms-2">{backups?.weekly.length || 0}</Badge>
              </span>
            </Card.Header>
            <Card.Body className="p-0">
              {renderBackupTable(backups?.weekly || [], 'weekly')}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-calendar-month me-2"></i>
                Monatliche Backups
                <Badge bg="secondary" className="ms-2">{backups?.monthly.length || 0}</Badge>
              </span>
            </Card.Header>
            <Card.Body className="p-0">
              {renderBackupTable(backups?.monthly || [], 'monthly')}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Info Alert */}
      <Alert variant="info" className="mt-3">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Backup-Inhalt:</strong> Datenbank, Asterisk-Konfiguration, Voicemail, Aufnahmen, benutzerdefinierte Sounds und Music-on-Hold.
        <br />
        <small className="text-muted">Speicherort: /var/backups/astgui/</small>
      </Alert>

      {/* Details Modal */}
      <Modal show={showDetails} onHide={() => setShowDetails(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-archive me-2"></i>
            Backup-Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBackup && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Dateiname:</strong><br />
                  <code>{selectedBackup.filename}</code>
                </Col>
                <Col md={6}>
                  <strong>Größe:</strong><br />
                  {selectedBackup.size_human}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Typ:</strong><br />
                  <Badge bg={
                    selectedBackup.type === 'daily' ? 'primary' :
                    selectedBackup.type === 'weekly' ? 'info' : 'success'
                  }>
                    {selectedBackup.type}
                  </Badge>
                </Col>
                <Col md={6}>
                  <strong>Erstellt:</strong><br />
                  {formatDate(selectedBackup.created)}
                </Col>
              </Row>

              {selectedBackup.meta && (
                <>
                  <hr />
                  <h6>Backup-Metadaten</h6>
                  <Row className="mb-2">
                    <Col md={6}>
                      <small className="text-muted">Hostname:</small><br />
                      {selectedBackup.meta.hostname}
                    </Col>
                    <Col md={6}>
                      <small className="text-muted">Asterisk-Version:</small><br />
                      {selectedBackup.meta.asterisk_version}
                    </Col>
                  </Row>

                  <h6 className="mt-3">Enthaltene Komponenten</h6>
                  <ListGroup>
                    {Object.entries(selectedBackup.meta.components).map(([key, value]) => (
                      <ListGroup.Item key={key} className="d-flex justify-content-between align-items-center py-2">
                        <span>
                          {key === 'database' && 'Datenbank'}
                          {key === 'asterisk_config' && 'Asterisk-Konfiguration'}
                          {key === 'voicemail' && 'Voicemail'}
                          {key === 'recordings' && 'Aufnahmen'}
                          {key === 'custom_sounds' && 'Benutzerdefinierte Sounds'}
                          {key === 'moh' && 'Music-on-Hold'}
                        </span>
                        {value ? (
                          <Badge bg="success"><i className="bi bi-check-lg"></i></Badge>
                        ) : (
                          <Badge bg="secondary"><i className="bi bi-x-lg"></i></Badge>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </>
              )}

              {selectedBackup.file_count !== undefined && (
                <div className="mt-3">
                  <small className="text-muted">Dateien im Archiv: {selectedBackup.file_count}</small>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetails(false)}>
            Schließen
          </Button>
          {selectedBackup && (
            <Button
              variant="success"
              onClick={() => handleDownload(selectedBackup.type, selectedBackup.filename)}
            >
              <i className="bi bi-download me-1"></i>
              Download
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Restore Modal */}
      <Modal show={showRestore} onHide={() => setShowRestore(false)} size="lg">
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>
            <i className="bi bi-arrow-counterclockwise me-2"></i>
            Backup wiederherstellen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {restoreBackup && (
            <>
              <Alert variant="warning" className="mb-4">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Achtung:</strong> Die Wiederherstellung überschreibt vorhandene Daten. Wählen Sie die gewünschten Komponenten sorgfältig aus.
              </Alert>

              <div className="mb-3">
                <strong>Backup:</strong> <code>{restoreBackup.filename}</code>
              </div>

              <h6 className="mb-3">
                <i className="bi bi-list-check me-2"></i>
                Komponenten auswählen
              </h6>

              <div className="mb-3">
                <Button variant="outline-primary" size="sm" className="me-2" onClick={selectAllComponents}>
                  Alle auswählen
                </Button>
                <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => selectCategoryComponents('config')}>
                  Nur Konfiguration
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setSelectedComponents([])}>
                  Auswahl aufheben
                </Button>
              </div>

              {['system', 'config', 'data'].map(category => (
                <div key={category} className="mb-3">
                  <div
                    className="d-flex align-items-center mb-2 cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => selectCategoryComponents(category)}
                  >
                    <i className={`bi ${getCategoryIcon(category)} me-2`}></i>
                    <strong>{getCategoryName(category)}</strong>
                    <Badge bg="secondary" className="ms-2">
                      {restoreComponents.filter(c => c.category === category).length}
                    </Badge>
                  </div>
                  <ListGroup>
                    {restoreComponents
                      .filter(c => c.category === category)
                      .map(component => (
                        <ListGroup.Item
                          key={component.id}
                          className="d-flex align-items-center py-2"
                          action
                          onClick={() => toggleComponent(component.id)}
                        >
                          <Form.Check
                            type="checkbox"
                            checked={selectedComponents.includes(component.id)}
                            onChange={() => toggleComponent(component.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="me-3"
                          />
                          <div className="flex-grow-1">
                            <div className="fw-bold">{component.name}</div>
                            <small className="text-muted">{component.description}</small>
                          </div>
                          {restoreResults && (
                            <div>
                              {restoreResults.find(r => r.component === component.id)?.success ? (
                                <Badge bg="success"><i className="bi bi-check-lg"></i></Badge>
                              ) : restoreResults.find(r => r.component === component.id) ? (
                                <Badge bg="danger"><i className="bi bi-x-lg"></i></Badge>
                              ) : null}
                            </div>
                          )}
                        </ListGroup.Item>
                      ))}
                  </ListGroup>
                </div>
              ))}

              {restoreResults && (
                <div className="mt-4">
                  <h6>
                    <i className="bi bi-terminal me-2"></i>
                    {previewMode ? 'Vorschau-Ergebnis' : 'Wiederherstellungs-Ergebnis'}
                  </h6>
                  <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {restoreResults.map((result, idx) => (
                      <div key={idx} className="mb-2">
                        <div className={result.success ? 'text-success' : 'text-danger'}>
                          {result.success ? '[OK]' : '[FEHLER]'} {result.component}
                        </div>
                        {result.output && (
                          <pre className="small mb-0 ms-4 text-muted">{result.output.substring(0, 500)}</pre>
                        )}
                        {result.error && (
                          <pre className="small mb-0 ms-4 text-danger">{result.error}</pre>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRestore(false)}>
            Abbrechen
          </Button>
          {previewMode ? (
            <>
              <Button
                variant="info"
                onClick={() => { setPreviewMode(true); handleRestore(); }}
                disabled={restoring || selectedComponents.length === 0}
              >
                {restoring ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-eye me-1"></i>}
                Vorschau
              </Button>
              <Button
                variant="warning"
                onClick={() => { setPreviewMode(false); handleRestore(); }}
                disabled={restoring || selectedComponents.length === 0}
              >
                {restoring ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-arrow-counterclockwise me-1"></i>}
                Wiederherstellen
              </Button>
            </>
          ) : (
            <Button
              variant="warning"
              onClick={handleRestore}
              disabled={restoring || selectedComponents.length === 0}
            >
              {restoring ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-arrow-counterclockwise me-1"></i>}
              Wiederherstellen
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Upload Modal */}
      <Modal show={showUpload} onHide={() => setShowUpload(false)}>
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="bi bi-upload me-2"></i>
            Backup hochladen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Laden Sie ein zuvor heruntergeladenes it64Pbx-Backup hoch, um es später wiederherstellen zu können.
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Backup-Datei (.tar.gz)</Form.Label>
            <Form.Control
              type="file"
              accept=".tar.gz,.tgz"
              onChange={(e) => {
                const input = e.target as HTMLInputElement
                setUploadFile(input.files?.[0] || null)
              }}
            />
            {uploadFile && (
              <Form.Text className="text-success">
                <i className="bi bi-check-circle me-1"></i>
                {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Backup-Kategorie</Form.Label>
            <Form.Select
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value)}
            >
              <option value="daily">Täglich</option>
              <option value="weekly">Wöchentlich</option>
              <option value="monthly">Monatlich</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Bestimmt, in welcher Liste das Backup angezeigt wird.
            </Form.Text>
          </Form.Group>

          <Alert variant="info" className="mb-0">
            <i className="bi bi-info-circle me-2"></i>
            Nach dem Hochladen können Sie das Backup über den Wiederherstellen-Button auswählen und einzelne Komponenten wiederherstellen.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpload(false)}>
            Abbrechen
          </Button>
          <Button
            variant="success"
            onClick={handleUpload}
            disabled={!uploadFile || uploading}
          >
            {uploading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Wird hochgeladen...
              </>
            ) : (
              <>
                <i className="bi bi-upload me-1"></i>
                Hochladen
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
